import { Box, Button, Chip, Collapse, TextField, Typography, useTheme } from "@mui/material";
import { Gavel } from "lucide-react";
import { useState } from "react";
import { useAppeals } from "../context/appeals.context";
import { AppealThread, AppealView, StepComment } from "../dtos";
import { AppealAnchor } from "../utils/appeals";
import {
  NoteAttachmentComposer,
  NoteAttachmentList,
  useNoteAttachmentDraft,
} from "./note-attachments";

/** The root line's headline — what was decided, and by whom (by NAME; the id never reaches a client). */
const appealHeadline = (appeal: AppealView | null | undefined): string =>
  `Appeal · ${appeal?.decision === "CONDITIONAL" ? "Conditional" : "Rejected"} by ${
    appeal?.deciderName ?? "the reviewer"
  }`;

const AppealReplyComposer = ({
  appealUid,
  label,
  busy,
  onSend,
}: {
  appealUid: string;
  label?: string;
  busy: boolean;
  onSend: (content: string, attachmentUids?: string[]) => Promise<unknown>;
}) => {
  const appeals = useAppeals();
  const [draft, setDraft] = useState("");
  const attachments = useNoteAttachmentDraft({
    caseUid: appeals.caseUid,
    context: {kind: "appeal-reply", threadUid: appealUid},
  });
  const send = () => {
    const content = draft.trim();
    if (!content && !attachments.attachmentUids.length) return;
    void onSend(content, attachments.attachmentUids).then(() => {
      attachments.markSubmitted();
      setDraft("");
    }, () => undefined);
  };
  return (
    <Box>
      <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
        <TextField
          fullWidth
          size="small"
          multiline
          maxRows={4}
          autoFocus
          placeholder="Reply on this appeal…"
          slotProps={{htmlInput: {"aria-label": label ? `Reply to appeal on ${label}` : "Reply to appeal"}}}
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
        />
        <Button
          size="small"
          variant="outlined"
          disabled={busy || attachments.pending || attachments.hasErrors || (!draft.trim() && !attachments.attachmentUids.length)}
          onClick={send}
        >
          Send
        </Button>
      </Box>
      <NoteAttachmentComposer draft={attachments} disabled={busy} />
    </Box>
  );
};

/**
 * §Review-appeal: the appeal on ONE item, rendered where that item is — a rejected file's own row, the
 * returned field under its reason, an owner's card on a two-sided document.
 *
 * Anchoring beats a step-level panel here for one reason: the decision being challenged is per item, and a
 * panel forces the agent to re-identify the item in a picker they just read on screen. It is also how
 * workflow-manager renders the same feature (`components/appeal-thread.tsx`), so the two surfaces read as
 * one system to sales and credit.
 *
 * Renders NOTHING unless there is a thread here or an appeal may be raised — so it is safe to place on
 * every row and every field, which is what keeps the gating in one place (the provider) instead of being
 * re-derived at each site.
 *
 * Existing threads render directly, with no disclosure to click through: an appeal is a challenge awaiting
 * an answer, and hiding it behind a chevron is how a conversation stalls. Only the empty "raise one"
 * affordance is a disclosure, so an item nobody has appealed costs one small button.
 */
const InlineAppeal = ({ anchor, label }: { anchor: AppealAnchor; label?: string }) => {
  const theme = useTheme();
  const colors = theme.nawy.colors.semantic;
  const appeals = useAppeals();
  const [draft, setDraft] = useState("");
  const [composing, setComposing] = useState(false);
  const [replyTo, setReplyTo] = useState<string | null>(null);
  const openAttachments = useNoteAttachmentDraft({
    caseUid: appeals.caseUid,
    context: {
      kind: "appeal",
      stepUid: appeals.stepUid,
      fieldKey: anchor.fieldKey,
      ...(anchor.uploadUid ? {uploadUid: anchor.uploadUid} : {}),
    },
  });

  const threads = appeals.threadsFor(anchor);
  const canOpen = appeals.canOpen(anchor);
  if (!threads.length && !canOpen) return null;

  // Clear only on SUCCESS — a refused write (409 duplicate, closed window) already toasts its reason, and
  // wiping the composer with it would cost the agent the paragraph they typed. Rejections are swallowed
  // here because the provider's onError owns the messaging.
  const sendAppeal = (): void => {
    void appeals.open(anchor, draft.trim(), openAttachments.attachmentUids).then(() => {
      openAttachments.markSubmitted();
      setDraft("");
      setComposing(false);
    }, () => undefined);
  };

  return (
    <Box sx={{ mt: 1 }}>
      {threads.map((thread: AppealThread) => {
        const root = thread.comments[0];
        const appeal = root?.appeal;
        return (
          <Box
            key={root?.uid}
            sx={{
              mb: 0.75,
              // A full edge, not just the rail: this block renders INSIDE a returned file's row, whose
              // own red tint is close enough to the appeal's amber that the rail alone left the
              // conversation looking like more of the rejection notice.
              border: `1px solid ${colors.border.primary}`,
              borderLeft: `3px solid ${colors.text.extension.warning}`,
              borderRadius: 1,
              overflow: "hidden",
            }}
          >
            {thread.comments.map((entry: StepComment, index: number) => (
              <Box
                key={entry.uid}
                sx={{
                  px: 1,
                  py: 0.75,
                  // The root wears the appeal's own ground; replies sit quieter beneath it, so the
                  // challenge and its answers read as one exchange rather than a stack of equal notes.
                  pl: index === 0 ? 1 : 2,
                  bgcolor:
                    index === 0
                      ? colors.layer.extension.warningLight
                      : colors.layer.secondary,
                }}
              >
                <Box
                  sx={{ display: "flex", gap: 1, alignItems: "baseline", flexWrap: "wrap" }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 700,
                      color: index === 0 ? colors.text.extension.warning : undefined,
                    }}
                  >
                    {index === 0 ? appealHeadline(appeal) : entry.actorName}
                  </Typography>
                  {index === 0 && (
                    <Typography variant="caption" color="text.secondary">
                      {entry.actorName}
                    </Typography>
                  )}
                  <Typography variant="caption" color="text.secondary">
                    {new Date(entry.createdAt).toLocaleString()}
                  </Typography>
                  {/* A superseded decision keeps its thread readable, but the label stops the exchange
                      reading as though it were still about the file currently on the step. */}
                  {index === 0 && !thread.isCurrentDecision && (
                    <Chip
                      size="small"
                      variant="outlined"
                      label="decision has since changed"
                      sx={{ height: 18, fontSize: 11 }}
                    />
                  )}
                  {/* §Appeal-resolution: an ACCEPTED appeal is itself what changed the decision, so the
                      chip above appears too — without this one, the good news would read as a bare
                      "changed" with no indication that the challenge is what won. */}
                  {index === 0 && thread.resolution && (
                    <Chip
                      size="small"
                      variant={thread.resolution.outcome === "ACCEPTED" ? "filled" : "outlined"}
                      color={thread.resolution.outcome === "ACCEPTED" ? "success" : "default"}
                      label={
                        thread.resolution.outcome === "ACCEPTED"
                          ? "Appeal accepted"
                          : "Appeal upheld"
                      }
                      sx={{ height: 18, fontSize: 11 }}
                    />
                  )}
                </Box>
                <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                  {entry.content}
                </Typography>
                <NoteAttachmentList attachments={entry.attachments} />
              </Box>
            ))}
            {/* §Appeal-resolution: the reviewer's answer, rendered from the thread envelope rather than as
                a reply — one record of the outcome, and a closed thread that cannot look like it is still
                waiting on the agent. The verdict itself is taken in the portal; this side only reads it. */}
            {thread.resolution && (
              <Box
                sx={{
                  px: 1,
                  py: 0.75,
                  bgcolor: colors.layer.secondary,
                  borderTop: `1px solid ${colors.border.primary}`,
                }}
              >
                <Box sx={{ display: "flex", gap: 1, alignItems: "baseline", flexWrap: "wrap" }}>
                  <Typography variant="caption" sx={{ fontWeight: 700 }}>
                    {thread.resolution.outcome === "ACCEPTED"
                      ? "Decision overturned"
                      : "Decision stands"}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {thread.resolution.resolvedByName ?? "The reviewer"} ·{" "}
                    {new Date(thread.resolution.resolvedAt).toLocaleString()}
                  </Typography>
                </Box>
                {thread.resolution.note && (
                  <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                    {thread.resolution.note}
                  </Typography>
                )}
                <NoteAttachmentList attachments={thread.resolution.attachments} />
              </Box>
            )}
            {/* The server's own gate, never a guess: offering a composer the POST would refuse costs the
                agent a typed paragraph and tells them nothing. */}
            {thread.canReply && root && (
              <Box sx={{ px: 1, py: 0.75 }}>
                {replyTo === root.uid ? (
                  <AppealReplyComposer
                    appealUid={root.uid}
                    label={label}
                    busy={appeals.busy}
                    onSend={(content, attachmentUids) =>
                      appeals.reply(root.uid, content, attachmentUids)
                    }
                  />
                ) : (
                  <Button size="small" disabled={appeals.busy} onClick={() => setReplyTo(root.uid)}>
                    Reply
                  </Button>
                )}
              </Box>
            )}
          </Box>
        );
      })}

      {canOpen && (
        <Box>
          <Button
            size="small"
            startIcon={<Gavel size={13} />}
            disabled={appeals.busy}
            aria-expanded={composing}
            // The panel this replaced could afford a sentence of explanation; one button per item
            // cannot, so the promise that made the feature worth using rides in the tooltip.
            title="Disagree? Challenge it — the reviewer who made this decision is notified and can reply."
            onClick={() => setComposing((value) => {
              if (value) openAttachments.discard();
              return !value;
            })}
            sx={{ textTransform: "none" }}
          >
            Appeal this decision
          </Button>
          <Collapse in={composing}>
            <Box sx={{ mt: 0.5, display: "flex", gap: 1, alignItems: "flex-start" }}>
              <TextField
                fullWidth
                size="small"
                multiline
                maxRows={4}
                placeholder="Why should this decision be reconsidered?"
                slotProps={{
                  htmlInput: {
                    "aria-label": label ? `Appeal ${label}` : "Appeal this decision",
                  },
                }}
                value={draft}
                onChange={(event) => setDraft(event.target.value)}
              />
              <Button
                size="small"
                variant="outlined"
                disabled={!draft.trim() || appeals.busy || openAttachments.pending || openAttachments.hasErrors}
                onClick={sendAppeal}
              >
                Send
              </Button>
            </Box>
            <NoteAttachmentComposer draft={openAttachments} disabled={appeals.busy} />
          </Collapse>
        </Box>
      )}
    </Box>
  );
};

export default InlineAppeal;
